import logger from '../../log';
import { v4 as uuidv4 } from 'uuid';
import { Observable, interval, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import retryWithBackoff from '../../operators/retryWithBackoff';
import { ConnectionChangeState } from '@kollective-frontend/ksdk-api';
import emitConnectionChanged from '../../common/emitConnectionChanged';

const log = logger.getLogger(__filename);

export interface EventBusConfig {
  pingInterval?: number;
  maxReconnectAttempts?: number;
  initRetryInterval?: number;
  maxRetryInterval?: number;
  shouldRetry?: (error: Error) => boolean;
}

export enum MessageType {
  SEND = 'send',
  PUBLISH = 'publish',
  RECEIVE = 'recv',
  PING = 'ping',
  REGISTER = 'register',
  UNREGISTER = 'unregister',
  ERROR = 'err'
}

type Headers = { [key: string]: string };

export interface Message<T> {
  type: MessageType;
  address?: string;
  headers?: Headers;
  body?: T;
  replyAddress?: string;
}

export enum STATE {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3
}

interface Handlers {
  onclose: (event: CloseEvent) => void;
  onerror: (error: Error) => void;
  onopen: (event: Event) => void;
  onreconnect: (event: Event) => void;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function deserialize(event: MessageEvent): any {
  const { data } = event;
  // we get binary data from the server
  if (data instanceof ArrayBuffer) {
    const decoder = new TextDecoder('utf8');
    return JSON.parse(decoder.decode(data));
  }
  // if its not binary, it's a string ...
  return JSON.parse(data);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export default class EventBus<T> {
  url: string;
  config: EventBusConfig;
  ws$: WebSocketSubject<Message<T>>;
  state: STATE;
  handlers: Handlers;
  pingSubscription: Subscription | undefined;
  retryCount = 0;

  constructor(url: string, config: EventBusConfig, handlers: Handlers) {
    this.url = url;
    this.config = config;
    this.state = STATE.CONNECTING;
    this.handlers = handlers;

    this.ws$ = webSocket({
      url,
      binaryType: 'arraybuffer',
      deserializer: deserialize,
      openObserver: {
        next: this.openHandler
      },
      closeObserver: {
        next: this.closeHandler
      }
    });

    this.ws$
      .pipe(
        retryWithBackoff(
          config.maxReconnectAttempts || 8,
          config.shouldRetry,
          config.initRetryInterval,
          config.maxRetryInterval
        )
      )
      .subscribe({
        next: () => {
          // unneeded next call
        },
        error: error => handleSubscribeError(error, this),
        complete: () => {
          this.state = STATE.CLOSED;
          log.info('Websocket connection closed');
        }
      });
  }

  createPing(): Observable<number> {
    this.cleanupPing();
    return interval(this.config.pingInterval || 5000).pipe(
      tap(() => {
        if (!this.ws$.closed) {
          this.ws$.next({
            type: MessageType.PING
          });
        }
      })
    );
  }

  cleanupPing(): void {
    if (this.pingSubscription) {
      this.pingSubscription.unsubscribe();
    }
  }

  openHandler = (event: Event): void => {
    this.state = STATE.OPEN;

    this.cleanupPing();
    this.pingSubscription = this.createPing().subscribe();

    if (this.retryCount < 1) {
      this.handlers.onopen(event);
    } else {
      this.handlers.onreconnect(event);
    }

    this.retryCount += 1;
  };

  closeHandler = (event: CloseEvent): void => {
    this.state = STATE.CLOSED;
    this.cleanupPing();
    this.handlers.onclose(event);
  };

  /**
   * Publish a message without the need of a response.
   */
  publish(address: string, message: T, headers?: Headers): void {
    const msg = {
      address: address,
      type: MessageType.PUBLISH,
      body: message,
      headers: headers ? headers : {}
    };
    this.ws$.next(msg);
  }

  /**
   * Send a message without the need of a response.
   */
  send(address: string, message: T, headers?: Headers): void {
    const msg = {
      address,
      type: MessageType.SEND,
      body: message,
      headers: headers ? headers : {}
    };
    this.ws$.next(msg);
  }

  /**
   * Send a message with the intent of a response.
   */
  request(
    address: string,
    message: T,
    headers?: Headers
  ): Observable<Message<T>> {
    return new Observable(subscriber => {
      const replyAddress = uuidv4();

      const subscription = this.ws$.subscribe({
        next: message => {
          if (message.address === replyAddress) {
            if (message.type == MessageType.ERROR) {
              // handle errors
              subscriber.error(message);
            } else {
              // send message as reply
              subscriber.next(message);
              // complete subscriber
              subscriber.complete();
              // unsubscribe from websocket.
              subscription.unsubscribe();
            }
          }
        },
        error: err => {
          subscriber.error(err);
          subscription.unsubscribe();
        }
      });

      const msg = {
        address,
        type: MessageType.SEND,
        body: message,
        headers: headers ? headers : {},
        replyAddress
      };
      this.ws$.next(msg);
    });
  }

  requestStream(
    address: string,
    message: T,
    headers?: Headers
  ): Observable<Message<T>> {
    return new Observable(subscriber => {
      const replyAddress = `rtc.${uuidv4()}`;

      const subscription = this.listen(replyAddress).subscribe({
        next: message => {
          if (message.type == MessageType.ERROR) {
            // handle errors
            subscriber.error(message);
          } else {
            // undefined, null, or empty objects signal completion.
            if (!message.body || Object.keys(message.body).length == 0) {
              // complete subscriber
              subscriber.complete();
              // unsubscribe from websocket.
              subscription.unsubscribe();
            } else {
              // send message as reply
              subscriber.next(message);
            }
          }
        },
        error: err => {
          subscriber.error(err);
          // unsub listener
          subscription.unsubscribe();
        },
        complete: () => {
          // unsub listener
          subscription.unsubscribe();
        }
      });

      const msg = {
        address,
        type: MessageType.SEND,
        body: message,
        headers: { ...headers, replyAddress }
      };
      this.ws$.next(msg);
    });
  }

  /**
   * Listen manages the register and unregister flow for the event bus.
   */
  listen(address: string, headers?: Headers): Observable<Message<T>> {
    const register = {
      address: address,
      type: MessageType.REGISTER,
      headers: headers ? headers : {}
    };

    const unregister = {
      address: address,
      type: MessageType.UNREGISTER,
      headers: headers ? headers : {}
    };
    return this.ws$.multiplex(
      // when caller subscribes
      () => register,
      // when caller unsubscribes
      () => unregister,
      // message filter
      message => message.address === address
    );
  }

  close(): void {
    this.cleanupPing();
    this.ws$.complete();
  }
}

export const handleSubscribeError = <T>(
  error: Error,
  bus: EventBus<T>
): void => {
  const closeEvent = error as unknown as CloseEvent;
  bus.state = STATE.CLOSING;
  const message = JSON.stringify(error, [
    'message',
    'arguments',
    'type',
    'name',
    'wasClean'
  ]);
  log.error(`Websocket error (${bus.url}): ${message}`, error);
  const state = ConnectionChangeState.CLOSE;
  const type = 'webrtc-agent';
  emitConnectionChanged({ message, state, type, closeEvent });
  bus.handlers.onerror(error);
  bus.state = STATE.CLOSING;
};
